import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import {
  DownloadIcon,
  RefreshCwIcon,
  FileSpreadsheetIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  CalendarIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  getOrgTaxFormsApi,
  getOrgTaxStatusApi,
  downloadOrgTaxPackageApi,
  type OrgTaxFormRecord,
  type OrgTaxStatusEntry,
} from '@/lib/api'
import { BRAND_MINT } from '@/lib/utils'

function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—'
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  })
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export const Route = createFileRoute('/dashboard/tax-forms')({
  component: () => {
    const currentYear = new Date().getFullYear()
    const [year, setYear] = useState(currentYear)
    const [status, setStatus] = useState<{ complete: number; pending: number; total: number } | null>(null)
    const [_statusRows, setStatusRows] = useState<OrgTaxStatusEntry[]>([])
    const [records, setRecords] = useState<OrgTaxFormRecord[]>([])
    const [loading, setLoading] = useState(true)
    const [downloading, setDownloading] = useState(false)

    const fetchData = useCallback(
      async (signal?: { cancelled: boolean }) => {
        setLoading(true)
        try {
          const [statusRes, recordsRes] = await Promise.all([
            getOrgTaxStatusApi(year),
            getOrgTaxFormsApi(year),
          ])
          if (signal?.cancelled) return
          setStatus({ complete: statusRes.complete, pending: statusRes.pending, total: statusRes.total })
          setStatusRows(statusRes.data)
          setRecords(recordsRes.data)
        } catch (err) {
          if (signal?.cancelled) return
          toast.error(err instanceof Error ? err.message : 'Failed to load tax data')
        } finally {
          if (!signal?.cancelled) setLoading(false)
        }
      },
      [year],
    )

    useEffect(() => {
      const signal = { cancelled: false }
      fetchData(signal)
      return () => {
        signal.cancelled = true
      }
    }, [fetchData])

    const handleDownload = async () => {
      setDownloading(true)
      try {
        const res = await downloadOrgTaxPackageApi(year)
        if (res.row_count === 0) {
          toast.info('No tax records generated for this year yet.')
          return
        }
        downloadCsv(res.filename, res.csv_content)
        toast.success(`Downloaded ${res.row_count} tax record${res.row_count === 1 ? '' : 's'}`)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Download failed')
      } finally {
        setDownloading(false)
      }
    }

    const completePct = status && status.total > 0 ? Math.round((status.complete / status.total) * 100) : 0

    return (
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h1 className="font-['Source_Serif_4',ui-serif,Georgia,serif] text-2xl font-semibold text-foreground">
            Annual Tax Data
          </h1>
          <p className="text-sm text-muted-foreground">
            PeerPass generates annual tax data for your employee declaration. Your organization files its own
            annual employee tax declaration with the relevant authority.
          </p>
        </div>

        {/* Year + actions */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarIcon className="size-4" />
            Tax year
          </label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:border-[#00E5A0] focus-visible:outline-none"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <div className="flex-1" />

          <Button
            variant="outline"
            onClick={() => fetchData()}
            className="text-muted-foreground hover:text-foreground"
          >
            <RefreshCwIcon className="size-4" />
            Refresh
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading || loading}
            className="bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold"
          >
            <DownloadIcon className="size-4" />
            {downloading ? 'Preparing…' : 'Download data package (CSV)'}
          </Button>
        </div>

        {/* Completeness panel */}
        {status && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Employees</span>
                <span className="text-lg font-semibold text-foreground">{status.total}</span>
              </div>
            </div>
            <div className="rounded-xl border border-[#00E5A0]/20 bg-[#00E5A0]/[0.05] p-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs text-emerald-600/80 dark:text-emerald-400/80">
                  <CheckCircleIcon className="size-3.5" /> Ready for declaration
                </span>
                <span className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{status.complete}</span>
              </div>
            </div>
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs text-amber-600/80 dark:text-amber-400/80">
                  <AlertTriangleIcon className="size-3.5" /> Incomplete
                </span>
                <span className="text-lg font-semibold text-amber-600 dark:text-amber-400">{status.pending}</span>
              </div>
            </div>
          </div>
        )}

        {/* Completeness progress */}
        {status && status.total > 0 && (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Annual declaration readiness</span>
              <span className="text-foreground font-medium">{completePct}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${completePct}%`, backgroundColor: BRAND_MINT }}
              />
            </div>
            <p className="text-xs text-muted-foreground/70 mt-2">
              An employee is ready when their tax ID is recorded and their {year} tax form has been generated.
            </p>
          </div>
        )}

        {/* Generated records table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <FileSpreadsheetIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-sm font-semibold text-foreground">Generated records — {year}</h2>
            </div>
            <span className="text-xs text-muted-foreground/70">{records.length} record{records.length === 1 ? '' : 's'}</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <span className="size-6 rounded-full border-2 border-border border-t-emerald-600 dark:border-t-emerald-400 animate-spin" />
            </div>
          ) : records.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">No tax records generated for {year}.</p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Records appear here once the annual generation run completes for your employees.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground/70 border-b border-border">
                    <th className="px-4 py-2.5 font-medium">Employee</th>
                    <th className="px-4 py-2.5 font-medium">Form</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium text-right">Total lent</th>
                    <th className="px-4 py-2.5 font-medium text-right">Total borrowed</th>
                    <th className="px-4 py-2.5 font-medium">Generated</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={`${r.user_id}-${r.form_code}`} className="border-b border-border/60 last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-2.5 text-foreground">{r.employee_name}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">{r.form_code}</span>
                        <span className="text-muted-foreground/70 text-xs ml-1.5">{r.form_name}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400">
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-foreground/80">{formatCents(r.total_lent_cents)}</td>
                      <td className="px-4 py-2.5 text-right text-foreground/80">{formatCents(r.total_borrowed_cents)}</td>
                      <td className="px-4 py-2.5 text-muted-foreground/70 text-xs">{formatDate(r.generated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Data-provider disclaimer */}
        <div className="rounded-xl border border-border bg-muted/30 p-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="text-muted-foreground font-medium">About this data:</span> PeerPass provides this annual tax
            data to support your organization's annual employee tax declaration. PeerPass does not file taxes on
            your behalf — your organization remains responsible for submitting the declaration to the relevant
            authority.
          </p>
        </div>
      </div>
    )
  },
})
